import { PermissionGuard } from '@/components/PermissionGuard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { UserInviteForm } from '@/components/UserInviteForm';
import { use404Redirect } from '@/hooks/use404Redirect';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useOrganizationMembers } from '@/hooks/useOrganizationMembers';
import { usePendingInvitations } from '@/hooks/usePendingInvitations';
import { UserPlus } from 'lucide-react';
import { useState } from 'react';
import { AddressesSection } from './organization/AddressesSection';
import { BillingCentersSection } from './organization/BillingCentersSection';
import { InvitationsSection } from './organization/InvitationsSection';
import { MembersSection } from './organization/MembersSection';
import { NotificationsSection } from './organization/NotificationsSection';

export function OrganizationManagementView() {
  const {
    data: organization,
    isLoading: isOrganizationLoading,
    isError: isOrganizationError,
    error: organizationError,
  } = useCurrentOrganization();
  const {
    data: pendingInvitations,
    isLoading: isInvitationsLoading,
    error: invitationsError,
  } = usePendingInvitations({
    organizationId: organization?.id,
  });
  const {
    data: members,
    isLoading: isMembersLoading,
    error: membersError,
  } = useOrganizationMembers({
    organizationId: organization?.id,
  });

  use404Redirect(organizationError);
  use404Redirect(invitationsError);
  use404Redirect(membersError);

  const [isInviteDialogOpen, setIsInviteDialogOpen] = useState(false);

  if (isOrganizationLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
      </div>
    );
  }

  if (isOrganizationError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-500 text-lg">
          Erreur lors du chargement de l'organisation
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">
            {organization?.name}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Paramètres de l'organisation
          </p>
        </div>
        <PermissionGuard permission="invite">
          <Dialog
            open={isInviteDialogOpen}
            onOpenChange={setIsInviteDialogOpen}
          >
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-4 shadow-sm hover:shadow-md transition-all">
                <UserPlus className="h-3.5 w-3.5" />
                Inviter un membre
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Inviter un utilisateur</DialogTitle>
                <DialogDescription>
                  Invitez un nouvel utilisateur à rejoindre votre organisation
                </DialogDescription>
              </DialogHeader>
              <UserInviteForm
                onSuccess={() => setIsInviteDialogOpen(false)}
              />
            </DialogContent>
          </Dialog>
        </PermissionGuard>
      </div>

      {/* Members */}
      <MembersSection
        organization={organization}
        members={members}
        isMembersLoading={isMembersLoading}
      />

      {/* Pending invitations */}
      {pendingInvitations && pendingInvitations.length > 0 && (
        <InvitationsSection
          pendingInvitations={pendingInvitations}
          isLoading={isInvitationsLoading}
        />
      )}

      {/* Billing centers */}
      <BillingCentersSection organization={organization} />

      {/* Notifications */}
      <NotificationsSection
        organizationId={organization?.id}
        members={members}
      />

      {/* Addresses */}
      <AddressesSection organizationId={organization?.id} />
    </div>
  );
}
